import type { RouteInfo, TransportSelectorMappedResponse } from '@/scopes/prices/domain/transports/types'
import type { ApiFlight, FlightSelectorApiRawResponse, InboundApiFlight } from './types'
import { mapTripSummary } from '@/scopes/prices/infrastructure/transport/mapTripSummary'
import { mapPriceBreakdown } from '../mapPriceBreakdown'
import { TransportType } from '../urlBuilder/transportSelectorUrlBuilder'

export const UndefinedTime = '--:--'

export interface FlightSelectorResponseMapper {
  map: (roomSelectorResponse: FlightSelectorApiRawResponse) => TransportSelectorMappedResponse
}

export function createFlightSelectorResponseMapper(): FlightSelectorResponseMapper {
  return {
    map: (flightSelectorResponse: FlightSelectorApiRawResponse): TransportSelectorMappedResponse => {
      const mapAirline = (airlineId: string): { id: string, name: string } => {
        const airline = flightSelectorResponse.airlines.find(a => a.id === airlineId)
        return airline || { id: '', name: '' }
      }

      const getAirportById = (airportId: string): { code: string, name: string } => {
        const airport = flightSelectorResponse.airports.find(a => a.id === airportId)
        return airport ? { code: airport.id, name: airport.name } : { code: '', name: '' }
      }

      const mapFlight = (flight: ApiFlight): RouteInfo => {
        return {
          id: flight.flightId,
          airline: mapAirline(flight.airlineId),
          arrivalDate: {
            time: flight.isArrivalTimeSpecified ? flight.arrivalDate.formattedTime : UndefinedTime,
            fullDate: flight.arrivalDate.formattedDate,
          },
          departureDate: {
            time: flight.isDepartureTimeSpecified ? flight.departureDate.formattedTime : UndefinedTime,
            fullDate: flight.departureDate.formattedDate,
          },
          flightType: flight.isFlexibleTicket ? 'flexible' : 'standard',
          luggage: flight.luggage,
          price: flight.priceDiff?.raw ?? 0,
          route: {
            from: getAirportById(flight.departureAirportId),
            to: getAirportById(flight.arrivalAirportId),
          },
          stationName: null,
          remark: flight.remark,
          isDefault: flight.isSelected,
        }
      }

      const inboundRoutes = flightSelectorResponse.inboundFlights.map(mapFlight)

      const outboundRoutes = flightSelectorResponse.outboundFlights.map((flight) => {
        const outboundFlight = mapFlight(flight)
        outboundFlight.relatedRoutes = []
        flight.inboundRelatedFlights?.forEach((inboundFlight: InboundApiFlight) => {
          const inbound = inboundRoutes.find(inboundRoute => inboundRoute.id === inboundFlight.inboundFlightId)
          if (inbound) {
            const relatedInbound = {
              ...inbound,
              price: inboundFlight.priceDiff.raw,
              tripSummary: inboundFlight.tripSummary.map(mapTripSummary),
              priceBreakdown: mapPriceBreakdown(inboundFlight.priceBreakdown, inboundFlight.routePricePerPerson.raw),
            }

            outboundFlight.relatedRoutes?.push(relatedInbound)
          }
        })

        return outboundFlight
      })

      const tripSummary = flightSelectorResponse.tripSummary?.map(mapTripSummary)

      return {
        transportType: TransportType.Flight,
        outboundRoutes,
        inboundRoutes,
        tripSummary,
        paymentMethod: flightSelectorResponse?.paymentMethod,
      }
    },
  }
}
